// Asks central-api for the plan state again, so a lock lifts as soon as a
// payment has been confirmed instead of at the next sign-in.

import store from '../../store/store';
import { setEmployeeIdentity } from '../../store/reducers/authSlice';
import { fetchIdentity } from '../api/identity/identity-api';
import { fetchAppInit } from '../api/app-init';
import { selectIsPlanLocked } from './plan-lock-rules';

interface RefreshOptions {
  // How often to ask. A payment confirmation (webhook) can reach central-api a
  // moment after the browser is back, so the landing pages ask a few times.
  attempts?: number;
  intervalMs?: number;
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

let running: Promise<boolean> | null = null;

async function refresh({
  attempts = 1,
  intervalMs = 2500,
}: RefreshOptions): Promise<boolean> {
  const wasLocked = selectIsPlanLocked(store.getState());

  for (let attempt = 0; attempt < attempts; attempt++) {
    const { auth } = store.getState();
    const agencyKey = auth.agency?.accessToken;
    const sellerToken = auth.authorization.accessToken?.sellerToken;

    if (agencyKey) {
      try {
        // `/me` is the cheap read; the identity reducer applies its plan state.
        store.dispatch(setEmployeeIdentity(await fetchIdentity(agencyKey)));
      } catch {
        // Try again on the next round; the lock stays as it is.
      }
    } else if (sellerToken) {
      // A seller session without an employee key: seller-init is the only
      // source, and it applies the plan state itself.
      await fetchAppInit();
      return selectIsPlanLocked(store.getState());
    } else {
      break;
    }

    if (!selectIsPlanLocked(store.getState())) {
      break;
    }
    if (attempt < attempts - 1) {
      await wait(intervalMs);
    }
  }

  const locked = selectIsPlanLocked(store.getState());
  if (
    wasLocked &&
    !locked &&
    store.getState().auth.authorization.accessToken?.sellerToken
  ) {
    // The plan is back: load the subscription and brand data that the locked
    // session could not.
    await fetchAppInit();
  }
  return locked;
}

/**
 * Fetch the plan state again and apply it. Resolves to whether the session is
 * still locked. Calls made while one is running share its result.
 */
export function refreshPlanState(
  options: RefreshOptions = {}
): Promise<boolean> {
  if (!running) {
    running = refresh(options).finally(() => {
      running = null;
    });
  }
  return running;
}
